import { CompanyFormData, FormData, MSPFormData } from '../types';
import { getCookie } from '../util/cookies';
import { formatMarketingAttributionValueFromCookie } from '../util/formatMarketingAttributionValueFromCookie';
import { getAPIUrl } from '../util/getAPIUrl';
import { getExperimentVersion } from '../util/getExperimentVersion';
import { getGCLIDValue } from '../util/getGCLIDValue';
import optimizerABTestCookieValues from '../util/optimizerABTestCookieValues';
import { getTrialDates } from '../util/trialLength';

export const postSegmentService = async ({
  event,
  properties,
}: {
  event: string;
  properties: {
    reason: string;
    apiErrorDescription?: string;
    apiErrorCode?: string;
    sku?: string;
    journey_step?: string;
    source?: string;
  };
}) => {
  try {
    const response = await fetch(
      `${getAPIUrl()}/lmiapi/segment/send-event?experiment_version=${getExperimentVersion()}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          event,
          properties,
          platform: 'web',
        }),
      }
    );
    const resp = await response.json();
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

export const postBoomiService = async ({ data, userKey }: { data: FormData; userKey?: string }) => {
  try {
    const formattedMarketingAttributionObject = formatMarketingAttributionValueFromCookie();
    const abTestValues: string = optimizerABTestCookieValues();

    const dates = getTrialDates(data.trialLength);

    const params = new URLSearchParams(window.location.search);
    let isMax = params.has('max');

    const boomiData = {
      quantity: 1,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      country: getCookie('cciso') || '',
      optin: data.contactPermission,
      phone: data.phoneNumber,
      company: data.companyName,
      employees: data.companySize,
      language: data.language,
      title: data.jobTitle,
      ipAddress: data.clientIp,
      planType: data.flowType,
      trialStart: dates.trialStart,
      trialEnd: dates.trialEnd,
      mkto_trk: window._mkto_trk ? window!._mkto_trk : '',
      chop_id: getCookie('chop_id') || '',
      gclid: getGCLIDValue(),
      promoCode: '',
      accountKey: '',
      universalID: '',
      userKey: userKey,
      planKey: isMax ? 'LPBusinessSSOMFA' : data.trialSku,
      referringURL: window.location.href,
      programName: data.programName,
      programID: data.programID,
      ...formattedMarketingAttributionObject,
      ...(abTestValues ? { experiment: optimizerABTestCookieValues() } : {}),
    };
    const response = await fetch('/lpapi/form/SendTrialBoomi', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(boomiData),
    });
    await response.json();
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

export const postCompanyBoomiService = async ({ data, userKey }: { data: CompanyFormData; userKey: string }) => {
  try {
    const formattedMarketingAttributionObject = formatMarketingAttributionValueFromCookie();
    const abTestValues: string = optimizerABTestCookieValues();

    const boomiData = {
      quantity: 1,
      country: getCookie('cciso') || '',
      ipAddress: data.clientIp,
      planType: data.flowType,
      mkto_trk: window._mkto_trk ? window!._mkto_trk : '',
      chop_id: getCookie('chop_id') || '',
      gclid: getGCLIDValue(),
      promoCode: '',
      accountKey: '',
      universalID: '',
      userKey: userKey,
      planKey: data.trialSku,
      referringURL: window.location.href,
      programName: data.programName,
      programID: data.programID,
      ...formattedMarketingAttributionObject,
      ...(abTestValues ? { experiment: optimizerABTestCookieValues() } : {}),
    };
    const response = await fetch('/lpapi/form/SendTrialBoomi', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(boomiData),
    });
    await response.json();
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

export const postMSPBoomiService = async ({ data, userKey }: { data: MSPFormData; userKey: string }) => {
  try {
    const formattedMarketingAttributionObject = formatMarketingAttributionValueFromCookie();
    const abTestValues: string = optimizerABTestCookieValues();

    const dates = getTrialDates(data.trialLength);

    const boomiData = {
      quantity: 1,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      optin: data.contactPermission,
      phone: data.phoneNumber,
      company: data.companyName,
      employees: data.companySize,
      language: data.language,
      title: data.jobTitle,
      ipAddress: data.clientIp,
      planType: data.flowType,
      trialStart: dates.trialStart,
      trialEnd: dates.trialEnd,
      mkto_trk: window._mkto_trk ? window!._mkto_trk : '',
      chop_id: getCookie('chop_id') || '',
      gclid: getGCLIDValue(),
      promoCode: '',
      accountKey: '',
      universalID: '',
      userKey: userKey,
      planKey: data.trialSku,
      referringURL: window.location.href,
      zipCode: data.zipCode,
      country: data.country,
      state: data.state,
      managedUsers: data.numberOfManagedUsers,
      programName: data.programName,
      programID: data.programID,
      ...formattedMarketingAttributionObject,
      ...(abTestValues ? { experiment: optimizerABTestCookieValues() } : {}),
    };
    const response = await fetch('/lpapi/form/SendMSPTrialBoomi', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(boomiData),
    });
    await response.json();
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

export const postTrialInterestBoomiService = async ({
  email,
  contactPermission,
  trialSku,
  programName,
  programID,
}: {
  email: string;
  contactPermission: boolean;
  trialSku: string;
  programName?: string;
  programID?: string;
}) => {
  try {
    const formattedMarketingAttributionObject = formatMarketingAttributionValueFromCookie();
    const abTestValues: string = optimizerABTestCookieValues();

    const boomiData = {
      Email: email,
      country: getCookie('cciso') || '',
      optin: contactPermission,
      mkto_trk: window._mkto_trk ? window!._mkto_trk : '',
      chop_id: getCookie('chop_id') || '',
      gclid: getGCLIDValue(),
      promoCode: '',
      accountKey: '',
      universalID: '',
      productInterest: trialSku,
      referringURL: window.location.href,
      programName,
      programID,
      ...formattedMarketingAttributionObject,
      ...(abTestValues ? { experiment: optimizerABTestCookieValues() } : {}),
    };
    const response = await fetch('/lpapi/form/SendTrialInterest', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(boomiData),
    });
    await response.json();
    return { success: true };
  } catch (error) {
    console.warn('postTrialInterestBoomiService error:', error);
    return { success: false };
  }
};
